import { useCallback, useEffect, useRef, useState } from 'react'
import { getDemoResetSchedule } from '~/shared/api/services/demo-reset-schedule'
import { isDemoEnvironment } from '~/shared/lib/environment'
import { formatCountdown, getRemainingTimeMs } from './demo-reset-countdown'

type ScheduleState =
  | { status: 'loading' }
  | { status: 'unavailable' }
  | { status: 'disabled' }
  | {
      status: 'ready'
      serverTimeMs: number
      receivedAtMs: number
      nextResetAtMs: number
    }

export const useDemoResetSchedule = (): string | null => {
  const [schedule, setSchedule] = useState<ScheduleState>({ status: 'loading' })
  const [now, setNow] = useState(() => Date.now())
  const scheduleRef = useRef<ScheduleState>(schedule)
  const requestInProgress = useRef(false)
  const mounted = useRef(false)

  const updateSchedule = useCallback(async () => {
    if (requestInProgress.current || !mounted.current) return

    requestInProgress.current = true
    try {
      const response = await getDemoResetSchedule()
      if (!mounted.current) return

      if (!response.enabled || !response.next_reset_at) {
        scheduleRef.current = { status: 'disabled' }
        setSchedule(scheduleRef.current)
        return
      }

      const serverTimeMs = Date.parse(response.server_time)
      const nextResetAtMs = Date.parse(response.next_reset_at)
      const receivedAtMs = Date.now()

      if (
        !Number.isFinite(serverTimeMs) ||
        !Number.isFinite(nextResetAtMs) ||
        nextResetAtMs <= serverTimeMs
      ) {
        scheduleRef.current = { status: 'unavailable' }
        setSchedule(scheduleRef.current)
        return
      }

      scheduleRef.current = {
        status: 'ready',
        serverTimeMs,
        receivedAtMs,
        nextResetAtMs,
      }
      setSchedule(scheduleRef.current)
      setNow(receivedAtMs)
    } catch {
      if (mounted.current) {
        scheduleRef.current = { status: 'unavailable' }
        setSchedule(scheduleRef.current)
      }
    } finally {
      requestInProgress.current = false
    }
  }, [])

  useEffect(() => {
    if (!isDemoEnvironment) return

    mounted.current = true
    updateSchedule()

    const intervalId = window.setInterval(() => {
      const currentTime = Date.now()
      setNow(currentTime)

      const currentSchedule = scheduleRef.current
      if (
        currentSchedule.status === 'ready' &&
        getRemainingTimeMs(currentSchedule, currentTime) === 0
      ) {
        updateSchedule()
      }
    }, 1000)

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        setNow(Date.now())
        updateSchedule()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      mounted.current = false
      window.clearInterval(intervalId)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [updateSchedule])

  if (!isDemoEnvironment) return null
  if (schedule.status === 'loading') return 'Загружаем расписание сброса…'
  if (schedule.status === 'unavailable')
    return 'Расписание сброса временно недоступно'
  if (schedule.status === 'disabled') return null

  const remainingMs = getRemainingTimeMs(schedule, now)
  if (remainingMs === 0) return 'Обновляем расписание сброса…'

  return `До планового сброса: ${formatCountdown(remainingMs)}`
}
